import { queryOptions } from '@tanstack/react-query';
import {
  listGuests,
  getGuest,
  listGuestBookings,
  getGuestConversations,
  listGuestContactChannels,
} from '@/api/generated/sdk.gen';
import type {
  Guest,
  ListGuestsResponse,
  GuestBooking,
  ListGuestBookingsResponse,
  ThreadSummary,
  ListThreadsResponse,
  GuestContactChannel,
} from '@/api/generated/types.gen';

export type {
  Guest,
  ListGuestsResponse,
  GuestBooking,
  ListGuestBookingsResponse,
  ThreadSummary,
  ListThreadsResponse,
  GuestContactChannel,
};

export function guestContactChannelsQuery(id: string) {
  return queryOptions({
    queryKey: ['guest-contact-channels', id] as const,
    queryFn: async ({ signal }) => {
      const res = await listGuestContactChannels({ throwOnError: true, signal, path: { id } });
      return res.data.items;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

export function guestsQuery(q: string, limit = 50) {
  return queryOptions({
    queryKey: ['guests', { q, limit }] as const,
    queryFn: async ({ signal }) => {
      const res = await listGuests({
        throwOnError: true,
        signal,
        query: { q: q || undefined, limit },
      });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

export function guestQuery(id: string) {
  return queryOptions({
    queryKey: ['guest', id] as const,
    queryFn: async ({ signal }) => {
      const res = await getGuest({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

export function guestBookingsQuery(id: string) {
  return queryOptions({
    queryKey: ['guest-bookings', id] as const,
    queryFn: async ({ signal }) => {
      const res = await listGuestBookings({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}

export function guestConversationsQuery(id: string) {
  return queryOptions({
    queryKey: ['guest-conversations', id] as const,
    queryFn: async ({ signal }) => {
      const res = await getGuestConversations({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && status !== 404 && failureCount < 1;
    },
    staleTime: 30_000,
  });
}
